<template>
  <SectionFrame v-if="links.length" :section="section" :index="index" :heading="section.title || ''" label="Links">
    <p v-if="section.subText" class="t-lead s2-links-sub">{{ section.subText }}</p>
    <ul :class="layout === 'cards' ? 's2-grid s2-links-cards' : 's2-links-list'" class="mo-stagger" :style="layout === 'cards' ? {'--s2-cols': 3} : null">
      <li v-for="l in links" :key="l.link">
        <a :href="back(l.link)" class="t-link" :class="layout === 'cards' ? 't-card s2-link-card' : 's2-link-line'"
           :target="external(l.link) ? '_blank' : null" :rel="external(l.link) ? 'noopener' : null">
          <img v-if="layout === 'cards' && previews[l.link]?.image" :src="previews[l.link].image" alt="" loading="lazy" decoding="async"
               class="s2-link-card__img" @error="previews[l.link].image = ''" />
          <span class="s2-link-text">
            <b>{{ l.label || previews[l.link]?.title || host(l.link) }}</b>
            <span v-if="previews[l.link]?.description" class="s2-link-desc s2-small">{{ previews[l.link].description }}</span>
          </span>
          <span class="s2-muted s2-small">{{ host(l.link) }}<span v-if="external(l.link)" class="s2-hp"> (opens a new tab)</span></span>
        </a>
      </li>
    </ul>
  </SectionFrame>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import SectionFrame from "./SectionFrame.vue";
import {layoutOf} from "@/utils/sectionLogic.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}});
const layout = computed(() => layoutOf(props.section, ["list", "cards"]));
const ok = (u) => /^(\/|https?:\/\/|mailto:|tel:)/i.test(String(u || ""));
const links = computed(() => (Array.isArray(props.section.links) ? props.section.links : []).filter((l) => l && ok(l.link)));
const external = (u) => /^https?:\/\//i.test(u);
// Link previews: each outside link's og: title, description and photo, fetched after the page loads
// (as the first Links section did), so a slow or missing site never holds up the page.
const previews = ref({});
const plain = (t) => (t ? new DOMParser().parseFromString(String(t), "text/html").documentElement.textContent.trim() : "");
const photo = (img, base) => { try { const u = new URL(img, base); return u.protocol === "https:" ? u.href : ""; } catch (e) { return ""; } };
onMounted(() => {
  const prefix = import.meta.env.VITE_IS_PREVIEW_SERVER === "true" ? "/api/preview" : "";
  links.value.filter((l) => external(l.link)).slice(0, 12).forEach(async (l) => {
    try {
      const r = await fetch(`${prefix}/api/proxy/link-preview?url=${encodeURIComponent(l.link)}`);
      if (!r.ok) return;
      const d = await r.json();
      previews.value = {...previews.value, [l.link]: {title: plain(d.title), description: plain(d.description), image: d.image ? photo(plain(d.image), l.link) : ""}};
    } catch (e) { /* no preview, the plain link stays */ }
  });
});
const host = (u) => { try { return external(u) ? new URL(u).hostname.replace(/^www\./, "") : u; } catch (e) { return u; } };
</script>

<style>
.s2-links-sub { margin: -1rem 0 1.5rem; }
.s2-links-list { list-style: none; padding: 0; margin: 0; max-width: 760px; }
.s2-link-line { display: flex; justify-content: space-between; gap: 1rem; padding: .9rem 0; border-bottom: 1px solid var(--t-line); }
.s2-links-cards { list-style: none; padding: 0; margin: 0; }
.s2-link-card { display: flex; flex-direction: column; gap: .3rem; height: 100%; box-sizing: border-box; }
.s2-link-text { display: flex; flex-direction: column; gap: .2rem; min-width: 0; }
.s2-link-desc { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; color: var(--t-muted, inherit); }
.s2-link-card__img { display: block; width: 100%; aspect-ratio: 1.91 / 1; object-fit: cover; border-radius: calc(var(--t-radius, 8px) * .75); margin-bottom: .5rem; }
.s2-link-line:hover b, .s2-link-card:hover b { text-decoration: underline; }
</style>
